import React, { useEffect, useState } from "react";
import { useSnackBar } from "../../../../../hooks/useSnackBar";

// components imports
import {
  DisconnectStoreDialog,
  RenameStoreDialog,
} from "./Components/StoreActionDialogs";
import SalesChannelsHeader from "./Components/SalesChannelsHeader";
import ComingSoonChannels from "./Components/ComingSoonChannels";
import ShopifyShowcase from "./Components/ShopifyShowcase";
import LegalNotice from "./Components/LegalNotice";
import ConnectedStores from "./Components/ConnectedStores";
import SegmentedControl from "../../../../../Components/UI/SegmentedControl/SegmentedControl";
import { isStoreActive } from "./salesChannels";

// apis imports
import {
  deleteMarketplaceAccount,
  fetchMarketplaceCompanies,
  updateMarketplaceAccountName,
  updateMarketplaceStatus,
} from "../../../../../services/ShipperServices/MarketPlaces/marketplaces.services";
// library imports

import { MdGridView, MdViewList } from "react-icons/md";
import { useTranslation } from "react-i18next";

const STORES_PER_PAGE = 100;

// Off until backend fixes the 404 on the store list endpoint and confirms
// Shopify app installs appear in it; until then the section would only ever
// show an error or an empty list. Flip to true to bring the section back.
const SHOW_CONNECTED_STORES = false;

const Marketplaces = () => {
  const { t } = useTranslation("settings");
  // Per store, so finishing one store's request never re-enables another
  // store's switch while that one is still saving.
  const [busy, setBusy] = useState({});
  const [renameTarget, setRenameTarget] = useState(null);
  const [disconnectTarget, setDisconnectTarget] = useState(null);
  const [dialogSaving, setDialogSaving] = useState(false);

  const [marketplaces, setMarketPlaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [view, setView] = useState("cards");

  const setSnack = useSnackBar();
  const showSnack = (text, severity) =>
    setSnack((snack) => ({ ...snack, open: true, text, severity }));

  const stores = marketplaces?.data ?? [];
  const syncingCount = stores.filter(isStoreActive).length;

  const viewToggle = (
    <SegmentedControl
      ariaLabel={t("marketplaces.viewAria")}
      value={view}
      onChange={setView}
      options={[
        { value: "cards", label: t("marketplaces.cards"), icon: MdGridView },
        { value: "list", label: t("marketplaces.list"), icon: MdViewList },
      ]}
    />
  );

  // The API paginates, but the page searches client-side with no pager, so
  // fetch one generous page; a shipper with more stores than this is unlikely.
  // A failed load gets its own state; leaving it as [] would read as "no
  // stores connected" and a 0 syncing count.
  const getAllMarketplaces = async () => {
    setLoading(true);
    setLoadError(false);
    const response = await fetchMarketplaceCompanies(1, STORES_PER_PAGE, "");
    setLoading(false);
    if (response.error) {
      setLoadError(true);
      return;
    }
    setMarketPlaces(response?.data?.data);
  };

  useEffect(() => {
    if (SHOW_CONNECTED_STORES) getAllMarketplaces();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The endpoint flips the status server-side, so the next value is the
  // opposite of what the row shows now.
  const handleStatusChange = async (store) => {
    const next = !isStoreActive(store);
    setBusy((prev) => ({ ...prev, [store.id]: true }));

    const updated = await updateMarketplaceStatus(parseInt(store.id));
    if (updated.error) {
      showSnack(
        updated.error?.response?.data?.message ?? updated.error.message,
        "error",
      );
    } else {
      showSnack(
        next
          ? t("marketplaces.syncResumed", { name: store.account_name })
          : t("marketplaces.syncPaused", { name: store.account_name }),
        next ? "success" : "info",
      );
      setMarketPlaces((prevState) => ({
        ...prevState,
        data: prevState?.data?.map((item) =>
          item.id === store.id ? { ...item, status: next } : item,
        ),
      }));
    }
    setBusy((prev) => ({ ...prev, [store.id]: false }));
  };

  // On failure the dialog stays open so the user can retry or cancel.
  const handleRename = async (store, accountName) => {
    setDialogSaving(true);
    const response = await updateMarketplaceAccountName(store.id, {
      account_name: accountName,
    });
    setDialogSaving(false);
    if (response.error) {
      showSnack(
        response.error?.response?.data?.message ??
          t("marketplaces.renameFailed"),
        "error",
      );
      return;
    }
    setMarketPlaces((prevState) => ({
      ...prevState,
      data: prevState?.data?.map((item) =>
        item.id === store.id ? { ...item, account_name: accountName } : item,
      ),
    }));
    showSnack(t("marketplaces.renamed", { name: accountName }), "success");
    setRenameTarget(null);
  };

  const handleDisconnect = async (store) => {
    setDialogSaving(true);
    const response = await deleteMarketplaceAccount(store.id);
    setDialogSaving(false);
    if (response.error) {
      showSnack(
        response.error?.response?.data?.message ??
          t("marketplaces.disconnectFailed", { name: store.account_name }),
        "error",
      );
      return;
    }
    setMarketPlaces((prevState) => ({
      ...prevState,
      data: prevState?.data?.filter((item) => item.id !== store.id),
    }));
    showSnack(
      t("marketplaces.disconnected", { name: store.account_name }),
      "success",
    );
    setDisconnectTarget(null);
  };

  return (
    <div className="min-h-full bg-ui-page">
      <RenameStoreDialog
        store={renameTarget}
        saving={dialogSaving}
        onSave={handleRename}
        onClose={() => setRenameTarget(null)}
      />
      <DisconnectStoreDialog
        store={disconnectTarget}
        saving={dialogSaving}
        onConfirm={handleDisconnect}
        onClose={() => setDisconnectTarget(null)}
      />
      <SalesChannelsHeader
        syncingCount={
          !SHOW_CONNECTED_STORES || loadError
            ? null
            : loading
              ? "–"
              : syncingCount
        }
      />

      <main className="mx-auto flex w-full max-w-[1400px] flex-col gap-[20px] px-[16px] pb-12 pt-[12px] sm:gap-7 sm:px-6 sm:pt-5">
        {SHOW_CONNECTED_STORES && (
          <ConnectedStores
            stores={stores}
            loading={loading}
            loadError={loadError}
            onRetry={getAllMarketplaces}
            busy={busy}
            onToggle={handleStatusChange}
            onRename={setRenameTarget}
            onDisconnect={setDisconnectTarget}
          />
        )}
        <ShopifyShowcase />
        <ComingSoonChannels view={view} viewToggle={viewToggle} />
        <LegalNotice />
      </main>
    </div>
  );
};

export default Marketplaces;
